import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import './SoulOrb.css';

// ── Dimension metadata ─────────────────────────────────────────────────────
const DIMENSIONS = [
  { key: 'identity',            label: 'Identity',          color: '#E8A87C', icon: '◉' },
  { key: 'values_beliefs',      label: 'Values & Beliefs',  color: '#85CDCA', icon: '◈' },
  { key: 'personality',         label: 'Personality',        color: '#D4A5FF', icon: '◇' },
  { key: 'emotional_landscape', label: 'Emotional',         color: '#FF6B6B', icon: '♡' },
  { key: 'relationships',       label: 'Relationships',     color: '#4ECDC4', icon: '∞' },
  { key: 'life_story',          label: 'Life Story',        color: '#FFE66D', icon: '☆' },
  { key: 'wisdom_perspective',  label: 'Wisdom',            color: '#A8E6CF', icon: '✧' },
  { key: 'voice_expression',    label: 'Expression',        color: '#FF8A5C', icon: '♪' },
];

// ── Core Particle Sphere ───────────────────────────────────────────────────
function CoreSphere({ overallScore }) {
  const meshRef = useRef();
  const particleCount = 2000;

  const positions = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 1.0 + (Math.random() - 0.5) * 0.15;
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    return pos;
  }, []);

  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      // Base: soft teal that brightens with score
      const brightness = 0.3 + overallScore * 0.7;
      col[i * 3] = 0.23 * brightness;
      col[i * 3 + 1] = 0.45 * brightness;
      col[i * 3 + 2] = 0.37 * brightness;
    }
    return col;
  }, [overallScore]);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.001;
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.05;
      // Pulsing scale
      const pulse = 1.0 + Math.sin(state.clock.elapsedTime * 0.8) * 0.02;
      meshRef.current.scale.setScalar(pulse);
    }
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={particleCount} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={particleCount} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={0.025}
        vertexColors
        transparent
        opacity={0.6 + overallScore * 0.4}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// ── Dimension Ring ─────────────────────────────────────────────────────────
function DimensionRing({ index, score, color, totalDimensions }) {
  const meshRef = useRef();
  const particleCount = 300;
  const ringRadius = 1.35 + index * 0.08;

  // Each ring is tilted at a unique angle
  const tiltAngle = (index / totalDimensions) * Math.PI;
  const tiltAxis = useMemo(() => {
    const a = (index / totalDimensions) * Math.PI * 2;
    return new THREE.Vector3(Math.cos(a), 0.3, Math.sin(a)).normalize();
  }, [index, totalDimensions]);

  const positions = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const wobble = (Math.random() - 0.5) * 0.06;
      pos[i * 3] = (ringRadius + wobble) * Math.cos(angle);
      pos[i * 3 + 1] = (Math.random() - 0.5) * 0.04;
      pos[i * 3 + 2] = (ringRadius + wobble) * Math.sin(angle);
    }
    return pos;
  }, [ringRadius]);

  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    const c = new THREE.Color(color);
    for (let i = 0; i < particleCount; i++) {
      const fade = 0.3 + score * 0.7;
      col[i * 3] = c.r * fade;
      col[i * 3 + 1] = c.g * fade;
      col[i * 3 + 2] = c.b * fade;
    }
    return col;
  }, [color, score]);

  useFrame((state) => {
    if (meshRef.current) {
      const speed = 0.15 + index * 0.05;
      meshRef.current.rotation.y += 0.002 * speed;
    }
  });

  // Only show particles proportional to score
  const visibleCount = Math.max(10, Math.floor(particleCount * Math.max(0.05, score)));

  return (
    <group rotation={new THREE.Euler().setFromQuaternion(
      new THREE.Quaternion().setFromAxisAngle(tiltAxis, tiltAngle)
    )}>
      <points ref={meshRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={visibleCount} array={positions.slice(0, visibleCount * 3)} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={visibleCount} array={colors.slice(0, visibleCount * 3)} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          size={0.02 + score * 0.015}
          vertexColors
          transparent
          opacity={0.2 + score * 0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>
    </group>
  );
}

// ── Inner Glow (ambient light sphere) ──────────────────────────────────────
function InnerGlow({ overallScore }) {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      const pulse = 0.8 + Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
      meshRef.current.scale.setScalar(pulse);
      meshRef.current.material.opacity = 0.05 + overallScore * 0.12;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1.1, 32, 32]} />
      <meshBasicMaterial
        color="#3b725f"
        transparent
        opacity={0.08}
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// ── The full 3D scene ──────────────────────────────────────────────────────
function OrbScene({ dimensions, overallScore }) {
  return (
    <>
      <ambientLight intensity={0.15} />
      <pointLight position={[0, 0, 0]} intensity={0.5 + overallScore * 1.5} color="#3b725f" />
      <InnerGlow overallScore={overallScore} />
      <CoreSphere overallScore={overallScore} />
      {DIMENSIONS.map((dim, i) => (
        <DimensionRing
          key={dim.key}
          index={i}
          score={dimensions[dim.key]?.score || 0}
          color={dim.color}
          totalDimensions={DIMENSIONS.length}
        />
      ))}
    </>
  );
}

// ── Main SoulOrb Component ─────────────────────────────────────────────────
export default function SoulOrb({ token, api, onClose, onSendMessage, onOpenWisdom }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aging, setAging] = useState(false);
  const [ageStatus, setAgeStatus] = useState(null);
  const [selectedDim, setSelectedDim] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [discoveryPrompts, setDiscoveryPrompts] = useState([]);
  // Review mode state
  const [reviewMode, setReviewMode] = useState(false);
  const [dimExtractions, setDimExtractions] = useState({});
  const [loadingExtractions, setLoadingExtractions] = useState(null);
  const [reviewStatus, setReviewStatus] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addingTrait, setAddingTrait] = useState(null); // dimension key or null
  const [newTraitKey, setNewTraitKey] = useState('');
  const [newTraitValue, setNewTraitValue] = useState('');
  const [actionPending, setActionPending] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${api}/soul_profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        if (data.last_dream?.status === 'running') {
          setAging(true);
          setAgeStatus('Aging...');
        } else {
          setAging(false);
        }
      }
    } catch (e) {
      console.error('Failed to fetch soul profile:', e);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const fetchDiscovery = useCallback(async () => {
    try {
      const res = await fetch(`${api}/soul_discovery?count=3`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDiscoveryPrompts(data.prompts || []);
      }
    } catch (e) {
      console.error('Failed to fetch discovery prompts:', e);
    }
  }, [api, token]);

  const fetchReviewStatus = useCallback(async () => {
    try {
      const res = await fetch(`${api}/soul_profile/review_status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReviewStatus(data.review_status || {});
      }
    } catch (e) {
      console.error('Failed to fetch review status:', e);
    }
  }, [api, token]);

  const fetchExtractions = useCallback(async (dimension) => {
    setLoadingExtractions(dimension);
    try {
      const res = await fetch(`${api}/soul_extractions?dimension=${dimension}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDimExtractions(prev => ({ ...prev, [dimension]: data.extractions || [] }));
      }
    } catch (e) {
      console.error('Failed to fetch extractions:', e);
    } finally {
      setLoadingExtractions(null);
    }
  }, [api, token]);

  useEffect(() => {
    fetchProfile();
    fetchDiscovery();
    fetchReviewStatus();
  }, [fetchProfile, fetchDiscovery, fetchReviewStatus]);

  // Poll while aging
  useEffect(() => {
    if (!aging) return;
    const interval = setInterval(() => {
      fetchProfile();
      setPollCount(c => c + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [aging, fetchProfile]);

  // Stop polling after aging completes
  useEffect(() => {
    if (profile?.last_dream?.status === 'completed' && aging) {
      setAging(false);
      setAgeStatus('Aging complete');
      fetchReviewStatus();
      setTimeout(() => setAgeStatus(null), 3000);
    }
  }, [profile, aging, fetchReviewStatus]);

  // Fetch extractions when selecting a dimension in review mode
  useEffect(() => {
    if (reviewMode && selectedDim && !dimExtractions[selectedDim]) {
      fetchExtractions(selectedDim);
    }
  }, [reviewMode, selectedDim, dimExtractions, fetchExtractions]);

  const triggerAge = async (forceFull = false) => {
    setAging(true);
    setAgeStatus('Initiating aging...');
    try {
      const res = await fetch(`${api}/dream?force_full=${forceFull}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAgeStatus('Aging...');
      }
    } catch (e) {
      console.error('Failed to trigger aging:', e);
      setAging(false);
      setAgeStatus('Aging failed');
    }
  };

  // ── Review actions ──────────────────────────────────────────────────────
  const confirmTrait = async (extractionId) => {
    setActionPending(extractionId);
    try {
      await fetch(`${api}/soul_extractions/${extractionId}`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ user_confirmed: true }),
      });
      if (selectedDim) await fetchExtractions(selectedDim);
      await recalculateScores();
    } catch (e) { console.error('Confirm failed:', e); }
    setActionPending(null);
  };

  const dismissTrait = async (extractionId) => {
    setActionPending(extractionId);
    try {
      await fetch(`${api}/soul_extractions/${extractionId}`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ dismissed: true }),
      });
      if (selectedDim) await fetchExtractions(selectedDim);
      await recalculateScores();
    } catch (e) { console.error('Dismiss failed:', e); }
    setActionPending(null);
  };

  const saveEdit = async (extractionId) => {
    if (!editValue.trim()) return;
    setActionPending(extractionId);
    try {
      await fetch(`${api}/soul_extractions/${extractionId}`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ trait_value: editValue.trim() }),
      });
      setEditingId(null);
      setEditValue('');
      if (selectedDim) await fetchExtractions(selectedDim);
      await recalculateScores();
    } catch (e) { console.error('Edit failed:', e); }
    setActionPending(null);
  };

  const addTrait = async (dimension) => {
    if (!newTraitKey.trim() || !newTraitValue.trim()) return;
    setActionPending('adding');
    try {
      await fetch(`${api}/soul_extractions`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          dimension,
          trait_key: newTraitKey.trim().toLowerCase().replace(/\s+/g, '_'),
          trait_value: newTraitValue.trim(),
        }),
      });
      setAddingTrait(null);
      setNewTraitKey('');
      setNewTraitValue('');
      await fetchExtractions(dimension);
      await recalculateScores();
    } catch (e) { console.error('Add trait failed:', e); }
    setActionPending(null);
  };

  const recalculateScores = async () => {
    try {
      await fetch(`${api}/soul_profile/recalculate`, {
        method: 'POST', headers: authHeaders,
      });
      await fetchProfile();
      await fetchReviewStatus();
    } catch (e) { console.error('Recalculate failed:', e); }
  };

  const overallScore = profile?.overall_score || 0;
  const dimensions = profile?.dimensions || {};

  // Compute depth from evidence_count (fallback if backend doesn't provide it)
  const computeDepth = (count) => count > 0 ? Math.round(Math.log2(1 + count * 1.5) * 10) / 10 : 0;
  const getDimDepth = (dimKey) => {
    const d = dimensions[dimKey];
    return d?.depth ?? computeDepth(d?.evidence_count || 0);
  };
  const overallDepth = profile?.overall_depth || DIMENSIONS.reduce((s, d) => s + getDimDepth(d.key), 0);

  const leftDims = DIMENSIONS.slice(0, 4);
  const rightDims = DIMENSIONS.slice(4);

  // ── Review status badge ────────────────────────────────────────────────
  const getReviewBadge = (dimKey) => {
    const rs = reviewStatus[dimKey];
    if (!rs || rs.total === 0) return null;
    if (rs.unreviewed === 0) return { label: 'Approved', cls: 'soul-review-approved' };
    return { label: `${rs.unreviewed} to review`, cls: 'soul-review-pending' };
  };

  const renderDimCard = (dim) => {
    const data = dimensions[dim.key] || { score: 0, evidence_count: 0, top_traits: [] };
    const isSelected = selectedDim === dim.key;
    const badge = getReviewBadge(dim.key);
    const exts = dimExtractions[dim.key] || [];
    const isLoadingExt = loadingExtractions === dim.key;

    return (
      <div
        key={dim.key}
        className={`soul-dim-card ${isSelected ? 'soul-dim-card-active' : ''}`}
        style={{ '--dim-color': dim.color }}
        onClick={() => {
          const next = isSelected ? null : dim.key;
          setSelectedDim(next);
          setEditingId(null);
          setAddingTrait(null);
          if (next && reviewMode && !dimExtractions[next]) fetchExtractions(next);
        }}
      >
        <div className="soul-dim-card-header">
          <span className="soul-dim-icon" style={{ color: dim.color }}>{dim.icon}</span>
          <span className="soul-dim-label">{dim.label}</span>
          {badge && <span className={`soul-review-badge ${badge.cls}`}>{badge.label}</span>}
          <span className="soul-dim-pct">{data.depth ?? computeDepth(data.evidence_count || 0)}</span>
        </div>

        {/* Expanded: Review mode shows all extractions; normal shows top traits */}
        {isSelected && reviewMode && (
          <div className="soul-dim-traits" onClick={e => e.stopPropagation()}>
            {isLoadingExt && <div className="soul-dim-empty">Loading traits...</div>}
            {!isLoadingExt && exts.length === 0 && (
              <div className="soul-dim-empty">No traits captured yet</div>
            )}
            {!isLoadingExt && exts.map((ext) => (
              <div
                key={ext.id}
                className={`soul-dim-trait soul-trait-reviewable ${ext.user_confirmed ? 'soul-trait-confirmed' : ''} ${ext.dismissed ? 'soul-trait-dismissed' : ''}`}
              >
                <div className="soul-trait-header">
                  <span className="soul-dim-trait-key">{ext.trait_key?.replace(/_/g, ' ')}</span>
                  <span className="soul-trait-conf">{Math.round((ext.confidence || 0) * 100)}%</span>
                </div>
                {editingId === ext.id ? (
                  <div className="soul-trait-edit-row">
                    <input
                      className="soul-trait-edit-input"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(ext.id); if (e.key === 'Escape') setEditingId(null); }}
                      autoFocus
                    />
                    <button className="soul-trait-action soul-trait-save" onClick={() => saveEdit(ext.id)} disabled={actionPending === ext.id}>Save</button>
                    <button className="soul-trait-action soul-trait-cancel" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                ) : (
                  <span className="soul-dim-trait-value">{ext.trait_value}</span>
                )}
                {editingId !== ext.id && (
                  <div className="soul-trait-actions">
                    {!ext.user_confirmed && !ext.dismissed && (
                      <button className="soul-trait-action soul-trait-confirm" onClick={() => confirmTrait(ext.id)} disabled={actionPending === ext.id} title="Confirm">✓</button>
                    )}
                    {ext.user_confirmed && <span className="soul-trait-status-tag confirmed">Confirmed</span>}
                    <button className="soul-trait-action soul-trait-edit-btn" onClick={() => { setEditingId(ext.id); setEditValue(ext.trait_value); }} title="Edit">✎</button>
                    {!ext.dismissed && (
                      <button className="soul-trait-action soul-trait-dismiss" onClick={() => dismissTrait(ext.id)} disabled={actionPending === ext.id} title="Dismiss">✕</button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* Add trait */}
            {addingTrait === dim.key ? (
              <div className="soul-add-trait-form" onClick={e => e.stopPropagation()}>
                <input className="soul-trait-edit-input" placeholder="trait_key (e.g. humor_style)" value={newTraitKey} onChange={e => setNewTraitKey(e.target.value)} />
                <input className="soul-trait-edit-input" placeholder="Trait description..." value={newTraitValue} onChange={e => setNewTraitValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTrait(dim.key); }} />
                <div className="soul-add-trait-btns">
                  <button className="soul-trait-action soul-trait-save" onClick={() => addTrait(dim.key)} disabled={actionPending === 'adding'}>Add</button>
                  <button className="soul-trait-action soul-trait-cancel" onClick={() => { setAddingTrait(null); setNewTraitKey(''); setNewTraitValue(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="soul-add-trait-btn" onClick={(e) => { e.stopPropagation(); setAddingTrait(dim.key); }}>+ Add trait</button>
            )}
          </div>
        )}

        {/* Normal mode: just show top traits */}
        {isSelected && !reviewMode && data.top_traits && data.top_traits.length > 0 && (
          <div className="soul-dim-traits">
            {data.top_traits.map((trait, i) => (
              <div key={i} className="soul-dim-trait">
                <span className="soul-dim-trait-key">{trait.key?.replace(/_/g, ' ')}</span>
                <span className="soul-dim-trait-value">{trait.value}</span>
                {trait.confirmed && <span className="soul-trait-status-tag confirmed">Confirmed</span>}
              </div>
            ))}
          </div>
        )}
        {isSelected && !reviewMode && (!data.top_traits || data.top_traits.length === 0) && (
          <div className="soul-dim-empty">
            No traits captured yet — keep chatting to fill this dimension
          </div>
        )}
      </div>
    );
  };

  // Count total unreviewed
  const totalUnreviewed = Object.values(reviewStatus).reduce((sum, r) => sum + (r.unreviewed || 0), 0);

  return (
    <div className="soul-orb-container">
      {/* Close button */}
      <button className="soul-orb-close" onClick={onClose}>✕</button>

      {/* Header */}
      <div className="soul-orb-header">
        <h2 className="soul-orb-title">Soul Capture</h2>
        <p className="soul-orb-subtitle">
          {reviewMode ? 'Review and refine your soul profile' : 'Your soul deepens with every conversation'}
        </p>
      </div>

      {/* Full-screen 3-column layout */}
      <div className="soul-orb-main">
        {/* Left column — first 4 dimensions */}
        <div className="soul-orb-col soul-orb-col-left">
          {leftDims.map(renderDimCard)}
        </div>

        {/* Center column — orb + age + discovery */}
        <div className="soul-orb-col soul-orb-col-center">
          <div className="soul-orb-canvas-wrap">
            {loading ? (
              <div className="soul-orb-loading">
                <div className="soul-orb-loading-orb" />
                <span>Loading soul profile...</span>
              </div>
            ) : (
              <Canvas
                camera={{ position: [0, 0, 3.5], fov: 50 }}
                gl={{ antialias: true, alpha: true }}
                style={{ background: 'transparent' }}
              >
                <OrbScene dimensions={dimensions} overallScore={overallScore} />
              </Canvas>
            )}

            {/* Overall depth overlay */}
            {!loading && (
              <div className="soul-orb-score-overlay">
                <div className="soul-orb-score-value">{Math.round(overallDepth)}</div>
                <div className="soul-orb-score-label">Soul Depth</div>
              </div>
            )}
          </div>

          {/* Action buttons: Age + Review toggle */}
          <div className="soul-orb-actions">
            {aging ? (
              <div className="soul-orb-dreaming-indicator">
                <div className="soul-orb-dreaming-pulse" />
                <span>{ageStatus || 'Aging...'}</span>
              </div>
            ) : (
              <>
                <button className="soul-orb-dream-btn" onClick={() => triggerAge(false)}>
                  <span className="soul-orb-dream-icon">⏳</span>
                  <span>Age</span>
                </button>
                <button
                  className={`soul-orb-review-btn ${reviewMode ? 'active' : ''}`}
                  onClick={() => setReviewMode(r => !r)}
                >
                  <span className="soul-orb-dream-icon">✎</span>
                  <span>Review</span>
                  {totalUnreviewed > 0 && !reviewMode && (
                    <span className="soul-review-count">{totalUnreviewed}</span>
                  )}
                </button>
                {onOpenWisdom && (
                  <button className="soul-orb-wisdom-btn" onClick={onOpenWisdom}>
                    <span className="soul-orb-dream-icon">✧</span>
                    <span>Wisdom</span>
                  </button>
                )}
              </>
            )}
            {profile?.last_dream?.memories_processed > 0 && !aging && (
              <span className="soul-orb-last-dream">
                Last aged: {profile.last_dream.memories_processed} memories analyzed
              </span>
            )}
          </div>

          {/* Discovery Prompts (hidden in review mode) */}
          {!reviewMode && discoveryPrompts.length > 0 && (
            <div className="soul-discovery">
              <div className="soul-discovery-header">
                <span className="soul-discovery-title">Deepen Your Soul</span>
                <span className="soul-discovery-sub">Answer these to strengthen weak dimensions</span>
              </div>
              {discoveryPrompts.map((p, i) => (
                <button
                  key={i}
                  className="soul-discovery-prompt"
                  style={{ '--dim-color': DIMENSIONS.find(d => d.key === p.dimension)?.color || '#3b725f' }}
                  onClick={() => {
                    if (onSendMessage) {
                      onSendMessage(p.prompt);
                      onClose();
                    }
                  }}
                >
                  <span className="soul-discovery-prompt-dim">{p.label} • Depth {computeDepth(p.evidence_count || Math.round(p.score * 10))}</span>
                  <span className="soul-discovery-prompt-text">{p.prompt}</span>
                </button>
              ))}
            </div>
          )}

          {/* Review mode instruction */}
          {reviewMode && (
            <div className="soul-review-hint">
              Click a dimension to review its traits. Confirm accurate traits, edit inaccuracies, or dismiss wrong ones. Your feedback improves the soul profile.
            </div>
          )}
        </div>

        {/* Right column — last 4 dimensions */}
        <div className="soul-orb-col soul-orb-col-right">
          {rightDims.map(renderDimCard)}
        </div>
      </div>
    </div>
  );
}
